<template>
    <div class="modal voice-modal open">
        <div class="voice-modal-content">

            <h1>Vérification des informations dictées</h1>

            <p class="voice-warning">
                L'IA peut se tromper sur les éléments dictés — vérifiez chaque information avant de valider.
            </p>

            <div class="voice-transcript">
                <strong>Ce qui a été compris</strong>
                <textarea class="voice-transcript-input" v-model="editedTranscript" rows="3" :disabled="loading"></textarea>
                <a href="javascript: void 0" class="voice-reanalyze"
                   :class="{disabled: loading || !transcriptChanged}"
                   @click="reanalyze">
                    <i class="icon-search"></i>
                    {{ loading ? 'Ré-analyse en cours…' : 'Corrigé ? Ré-analyser ce texte' }}
                </a>
            </div>

            <div class="voice-fields" v-if="visibleFields.length > 0">
                <label class="voice-chip" :class="{checked: checked[field.key]}" v-for="field in visibleFields" :key="field.key">
                    <input type="checkbox" v-model="checked[field.key]">
                    <span class="voice-chip-content">
                        <strong>{{ field.label }}</strong>
                        <span class="voice-chip-values">
                            <span class="voice-current">{{ formatValue(field, current[field.key]) }}</span>
                            <span class="voice-arrow">→</span>
                            <span class="voice-proposed">{{ formatValue(field, fields[field.key]) }}</span>
                        </span>
                    </span>
                    <i class="icon-valid voice-chip-check"></i>
                </label>
            </div>

            <div class="voice-bottle" v-if="bottleMatch">

                <div class="voice-card" v-if="bottleMatch.status === 'trouvee'">
                    <label class="voice-card-toggle" :class="{checked: bottleChecked}">
                        <input type="checkbox" v-model="bottleChecked">
                        <i class="icon-valid"></i>
                    </label>
                    <div class="voice-card-body">
                        <strong>Manipulation bouteille</strong>
                        <p>
                            {{ bottleMatch.typeOp.realType }} — bouteille <strong>{{ bottleMatch.bouteille.code }}</strong>
                            <span v-if="bottleMatch.bouteille.fluide"> ({{ bottleMatch.bouteille.fluide.nom }})</span>
                        </p>
                        <div class="voice-card-row">
                            Quantité
                            <input type="number" min="0" step="0.001" v-model.number="quantite" class="voice-quantite">
                            Kg
                        </div>
                    </div>
                </div>

                <div class="voice-info" v-else-if="bottleMatch.status === 'ineligible'">
                    <i class="icon-warning"></i>
                    Bouteille {{ bottleMatch.bouteille.code }} détectée mais non éligible pour "{{ typeOperationLabel }}"
                    (fluide différent, pleine ou inactive). Vérifiez manuellement dans "Manipulation de fluide".
                </div>

                <div class="voice-info" v-else-if="bottleMatch.status === 'fluide_manquant'">
                    <i class="icon-warning"></i>
                    Aucun fluide n'est associé à cette installation, la manipulation de bouteille dictée ne peut pas être proposée.
                </div>

                <div v-if="['introuvable', 'ambigu'].includes(bottleMatch.status)">
                    <div class="voice-info">
                        <i class="icon-warning"></i>
                        <span v-if="bottleMatch.status === 'introuvable'">Aucune bouteille correspondant au code dicté n'a été trouvée.</span>
                        <span v-else>Plusieurs bouteilles correspondent au code dicté.</span>
                        <span v-if="bottleMatch.eligibleBouteilles.length > 0"> Sélectionnez-la manuellement :</span>
                    </div>

                    <div class="voice-card" v-if="bottleMatch.eligibleBouteilles.length > 0">
                        <label class="voice-card-toggle" :class="{checked: bottleChecked}">
                            <input type="checkbox" v-model="bottleChecked">
                            <i class="icon-valid"></i>
                        </label>
                        <div class="voice-card-body">
                            <select v-model="manualBouteilleId" class="voice-select">
                                <option :value="null" disabled>Sélectionnez une bouteille</option>
                                <option v-for="b in bottleMatch.eligibleBouteilles" :key="b.id" :value="b.id">
                                    {{ b.code }} — {{ b.fluide ? b.fluide.nom : 'Indéfini' }} — {{ b.poids_net }}/{{ b.poids_max }} Kg
                                </option>
                            </select>
                            <div class="voice-card-row">
                                Quantité
                                <input type="number" min="0" step="0.001" v-model.number="quantite" class="voice-quantite">
                                Kg
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <div class="voice-card" v-if="fourniture">
                <label class="voice-card-toggle" :class="{checked: fournitureChecked}">
                    <input type="checkbox" v-model="fournitureChecked">
                    <i class="icon-valid"></i>
                </label>
                <div class="voice-card-body">
                    <strong>Ajout d'une fourniture</strong>
                    <p>
                        {{ fourniture.designation || '(désignation manquante)' }}
                        <span v-if="fourniture.etat"> — {{ fourniture.etat }}</span>
                        <span v-if="fourniture.quantite"> — Qté {{ fourniture.quantite }}</span>
                        <span v-if="fourniture.prix_unitaire"> — {{ fourniture.prix_unitaire }}€ HT</span>
                    </p>
                </div>
            </div>

            <div class="voice-actions">
                <a href="javascript: void 0" class="btn close" @click="$emit('close')">Annuler</a>
                <a href="javascript: void 0" class="btn confirm" @click="confirm">Valider la sélection</a>
            </div>

        </div>
    </div>
</template>

<script>

    const FIELD_DEFS = [
        {key: 'heure_arrivee', label: "Heure d'arrivée"},
        {key: 'heure_depart', label: 'Heure de départ'},
        {key: 'controle_periodique', label: "Contrôle d'étanchéité périodique", type: 'bool'},
        {key: 'controle_non_periodique', label: "Contrôle d'étanchéité non périodique", type: 'bool'},
        {key: 'kilometrage', label: 'Kilométrage (Km)'},
        {key: 'travaux_effectues', label: 'Travaux effectués'},
        {key: 'observations', label: 'Observations'},
        {key: 'intervention_contractuelle', label: 'Intervention contractuelle', type: 'bool'},
        {key: 'description_demande', label: 'Description de la demande'},
        {key: 'assemblage', label: "Assemblage de l'équipement", type: 'bool'},
        {key: 'mise_en_service', label: "Mise en service de l'équipement", type: 'bool'},
        {key: 'modification', label: "Modification de l'équipement", type: 'bool'},
        {key: 'maintenance', label: "Maintenance de l'équipement", type: 'bool'},
        {key: 'demantelement', label: 'Démantèlement', type: 'bool'},
        {key: 'autre_nature', label: 'Autre (nature)'},
        {key: 'observations_nature', label: 'Observations sur cerfa'},
        {key: 'temps_deplacement', label: 'Déplacement'},
        {key: 'temps_intervention', label: "Temps d'intervention"},
        {key: 'nombre_techniciens', label: 'Nombre de techniciens'},
        {key: 'noms_techniciens', label: 'Noms des techniciens'},
        {key: 'preventif', label: 'Préventif (visite d\'entretien)', type: 'bool'},
        {key: 'correctif', label: 'Correctif (dépannage)', type: 'bool'},
        {key: 'selon_devis', label: 'Selon devis', type: 'bool'},
        {key: 'garantie', label: 'Installation sous garantie', type: 'bool'},
        {key: 'travaux_termines', label: 'Travaux terminés', type: 'bool'},
        {key: 'devis_a_realiser', label: 'Devis à réaliser', type: 'bool'},
    ];

    export default {

        props: {
            transcript: {type: String, default: ''},
            fields: {type: Object, required: true},
            current: {type: Object, required: true},
            bottleMatch: {type: Object, default: null},
            loading: {type: Boolean, default: false},
        },

        data() {
            return {
                checked: {},
                bottleChecked: false,
                manualBouteilleId: null,
                quantite: null,
                fournitureChecked: false,
                editedTranscript: this.transcript,
            }
        },

        created() {
            this.resetSelection();
        },

        watch: {

            fields() {
                this.resetSelection();
            },

            bottleMatch() {
                this.resetSelection();
            },

            transcript(newVal) {
                this.editedTranscript = newVal;
            },

        },

        computed: {

            visibleFields() {
                return FIELD_DEFS.filter(f => this.fields[f.key] !== null && this.fields[f.key] !== undefined);
            },

            typeOperationLabel() {
                return this.bottleMatch && this.bottleMatch.typeOp ? this.bottleMatch.typeOp.realType : '';
            },

            manualBouteilleChoisie() {
                if (!this.bottleMatch || !this.manualBouteilleId) {
                    return null;
                }
                return this.bottleMatch.eligibleBouteilles.find(b => b.id === this.manualBouteilleId) || null;
            },

            fourniture() {
                return this.fields.fourniture || null;
            },

            transcriptChanged() {
                return this.editedTranscript.trim() !== '' && this.editedTranscript.trim() !== this.transcript.trim();
            },

        },

        methods: {

            resetSelection() {
                const checked = {};
                FIELD_DEFS.forEach(f => {
                    checked[f.key] = this.fields[f.key] !== null && this.fields[f.key] !== undefined;
                });

                this.checked = checked;
                this.bottleChecked = !!(this.bottleMatch && this.bottleMatch.status === 'trouvee');
                this.manualBouteilleId = null;
                this.quantite = this.bottleMatch && this.bottleMatch.quantiteProposee !== undefined
                    ? this.bottleMatch.quantiteProposee
                    : null;
                this.fournitureChecked = !!this.fields.fourniture;
            },

            reanalyze() {
                if (this.loading || !this.transcriptChanged) {
                    return;
                }
                this.$emit('reanalyze', this.editedTranscript);
            },

            formatValue(field, value) {
                if (value === null || value === undefined || value === '') {
                    return 'vide';
                }
                if (field.type === 'bool') {
                    return value ? 'oui' : 'non';
                }
                return value;
            },

            confirm() {
                const champs = {};

                this.visibleFields.forEach(field => {
                    if (this.checked[field.key]) {
                        champs[field.key] = this.fields[field.key];
                    }
                });

                let bouteille = null;

                if (this.bottleChecked && this.bottleMatch) {
                    const boutId = this.bottleMatch.status === 'trouvee'
                        ? this.bottleMatch.bouteille.id
                        : (this.manualBouteilleChoisie ? this.manualBouteilleChoisie.id : null);

                    if (boutId && this.quantite > 0) {
                        bouteille = {
                            bouteilleId: boutId,
                            typeOp: this.bottleMatch.typeOp,
                            quantite: this.quantite,
                        };
                    }
                }

                let fournitureSelection = null;

                if (this.fournitureChecked && this.fourniture && this.fourniture.designation && this.fourniture.quantite > 0) {
                    fournitureSelection = this.fourniture;
                }

                this.$emit('confirm', {champs, bouteille, fourniture: fournitureSelection});
            },

        },

    }

</script>

<style>

    .voice-modal {
        position: fixed;
        height: 100%;
        width: 100%;
        top: 0;
        left: 0;
        background-color: rgba(30, 30, 30, 0.75);
        z-index: 999;
        overflow-y: auto;
        padding-bottom: 40px;
    }

    .voice-modal-content {
        background-color: #fafafa;
        width: 90%;
        max-width: 500px;
        margin: 30px auto;
        padding: 20px;
        border-radius: 16px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
    }

    .voice-modal-content h1 {
        font-size: 1.8rem;
        font-weight: 700;
        color: #2b4c69;
        margin-bottom: 15px;
    }

    .voice-warning {
        background-color: #fff3cd;
        border: 1px solid #ffe69c;
        color: #664d03;
        padding: 8px 10px;
        border-radius: 8px;
        font-size: 1.1rem;
        margin-bottom: 15px;
    }

    .voice-transcript {
        background-color: #eef1f4;
        border-radius: 10px;
        padding: 12px;
        margin-bottom: 18px;
    }

    .voice-transcript strong {
        display: block;
        font-size: 1.1rem;
        text-transform: uppercase;
        letter-spacing: .03rem;
        color: #7a8a99;
        margin-bottom: 4px;
    }

    .voice-transcript-input {
        width: 100%;
        font-style: italic;
        color: #444;
        background-color: #fff;
        border: 1px solid #d5dbe0;
        border-radius: 8px;
        padding: 8px 10px;
        font-family: inherit;
        font-size: 1.3rem;
        resize: vertical;
    }

    .voice-transcript-input:disabled {
        opacity: .7;
    }

    .voice-reanalyze {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 8px;
        font-size: 1.2rem;
        color: #2b4c69;
        font-weight: 700;
    }

    .voice-reanalyze i {
        font-size: 1.6rem;
    }

    .voice-reanalyze.disabled {
        color: #aaa;
        cursor: default;
        pointer-events: none;
    }

    .voice-fields {
        margin-bottom: 4px;
    }

    .voice-chip {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        background-color: #ececec;
        color: #777;
        border-radius: 10px;
        padding: 10px 14px;
        margin-bottom: 10px;
        cursor: pointer;
        transition: background-color .15s ease, color .15s ease;
    }

    .voice-chip input {
        display: none;
    }

    .voice-chip.checked {
        background-color: #86bc24;
        color: #fff;
    }

    .voice-chip-content {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    .voice-chip-content strong {
        font-size: 1.1rem;
        text-transform: uppercase;
        letter-spacing: .03rem;
        opacity: .75;
    }

    .voice-chip-values {
        font-size: 1.4rem;
        margin-top: 2px;
        word-break: break-word;
    }

    .voice-current {
        text-decoration: line-through;
        opacity: .65;
        margin-right: 4px;
    }

    .voice-arrow {
        margin: 0 4px;
        opacity: .6;
    }

    .voice-proposed {
        font-weight: 700;
    }

    .voice-chip-check {
        font-size: 2.1rem;
        color: #bbb;
        flex-shrink: 0;
        transition: color .15s ease;
    }

    .voice-chip.checked .voice-chip-check {
        color: #fff;
    }

    .voice-card {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        background-color: #ececec;
        border-radius: 10px;
        padding: 12px 14px;
        margin-bottom: 10px;
    }

    .voice-card-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        border-radius: 50%;
        background-color: #d8d8d8;
        color: #999;
        flex-shrink: 0;
        cursor: pointer;
        transition: background-color .15s ease, color .15s ease;
    }

    .voice-card-toggle input {
        display: none;
    }

    .voice-card-toggle.checked {
        background-color: #86bc24;
        color: #fff;
    }

    .voice-card-toggle i {
        font-size: 1.9rem;
    }

    .voice-card-body {
        flex: 1;
        min-width: 0;
    }

    .voice-card-body strong {
        display: block;
        font-size: 1.1rem;
        text-transform: uppercase;
        letter-spacing: .03rem;
        color: #666;
        margin-bottom: 4px;
    }

    .voice-card-body p {
        margin: 0;
        font-size: 1.4rem;
        color: #333;
    }

    .voice-card-row {
        margin-top: 8px;
        font-size: 1.3rem;
        color: #333;
    }

    .voice-quantite {
        width: 70px;
        margin: 0 6px;
    }

    .voice-select {
        width: 100%;
        margin-bottom: 4px;
    }

    .voice-info {
        display: flex;
        align-items: center;
        gap: 8px;
        background-color: #fbe4e4;
        color: #a02020;
        border-radius: 10px;
        padding: 10px 14px;
        margin-bottom: 10px;
        font-size: 1.3rem;
    }

    .voice-info i {
        font-size: 1.8rem;
        flex-shrink: 0;
    }

    .voice-actions {
        display: flex;
        gap: 10px;
        margin-top: 20px;
    }

    .voice-actions .btn {
        flex: 1;
        display: block;
        text-align: center;
        padding: 14px;
        border-radius: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: .03rem;
        font-size: 1.3rem;
    }

    .voice-actions .btn.close {
        background-color: #e2e2e2;
        color: #666;
    }

    .voice-actions .btn.confirm {
        background: linear-gradient(135deg, #9ed13c, #6fa018);
        color: #fff;
        box-shadow: 0 4px 12px rgba(111, 160, 24, 0.4);
    }

</style>



// WEBPACK FOOTER //
// VoiceInterventionModal.vue?fc84f1c6